import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Skeleton } from '@/components/ui/skeleton'
import { useTranslations } from '@/hooks/use-translations'
import { useManagedAppsQuery } from '@/queries/meta'

/**
 * Meta apps the user manages (`manage_app_solution`): their role on each one
 * and the WhatsApp Multi-Partner Solutions each app takes part in, read on the
 * app's behalf.
 */
export function MetaAppsTab() {
  const { t } = useTranslations()
  const apps = useManagedAppsQuery()

  if (apps.isLoading) return <Skeleton className="h-48 w-full" />
  if (apps.error) return <p className="text-sm text-destructive">{t('meta_apps.load_error')}: {apps.error.message}</p>

  return (
    <div className="space-y-8">
      <div className="space-y-1">
        <h2 className="text-lg font-semibold">{t('meta_apps.title')}</h2>
        <p className="text-sm text-muted-foreground">{t('meta_apps.description')}</p>
      </div>
      {apps.data?.length === 0 && <p className="text-sm text-muted-foreground">{t('meta_apps.empty')}</p>}
      <ul className="divide-y">
        {apps.data?.map((app) => (
          <li key={app.id} className="space-y-2 py-4">
            <div className="flex flex-wrap items-center gap-2">
              <span className="text-sm font-semibold">{app.name}</span>
              <span className="text-xs text-muted-foreground">{app.id}</span>
              {app.category && <Badge variant="outline">{app.category}</Badge>}
              {app.link && (
                <Button asChild variant="link" size="xs">
                  <a href={app.link} target="_blank" rel="noreferrer">{t('meta_apps.open')}</a>
                </Button>
              )}
            </div>
            <div className="flex flex-wrap items-center gap-2 text-xs">
              <span className="text-muted-foreground">{t('meta_apps.tasks')}:</span>
              {app.tasks.map((task) => <Badge key={task} variant="secondary">{task}</Badge>)}
            </div>
            <div className="flex flex-wrap items-center gap-2 text-xs">
              <span className="text-muted-foreground">{t('meta_apps.solutions')}:</span>
              {app.solutions.length === 0 && <span>{t('meta_apps.no_solutions')}</span>}
              {app.solutions.map((solution) => (
                <Badge key={solution.id} variant="outline">{solution.name} · {solution.status}</Badge>
              ))}
            </div>
          </li>
        ))}
      </ul>
    </div>
  )
}
